import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCart } from "@/lib/cart";
import { startCheckout, SHIPPING_FALLBACK_ID } from "@/lib/mercadopago.functions";
import { applyCoupon, type CouponValidationResult } from "@/lib/coupons.functions";
import { createAddress, myAddressesQuery, type CustomerAddress } from "@/lib/addresses.functions";
import { useAuth } from "@/lib/auth";
import {
  formatBRL,
  formatCPF,
  formatPhone,
  formatCEP,
  validateCPF,
  lookupCEP,
  whatsappLink,
} from "@/lib/format";
import { calculateShipping, type ShippingResult } from "@/lib/shipping/shipping.functions";
import { useStoreSettings } from "@/lib/settings.functions";
import { Loader2, Check, ShieldCheck, Tag, X, MessageCircle } from "lucide-react";

export const Route = createFileRoute("/checkout")({
  head: () => ({
    meta: [
      { title: "Checkout — Loja EG Elétrica" },
      { name: "description", content: "Finalize seu pedido com segurança." },
      { property: "og:title", content: "Checkout — Loja EG Elétrica" },
      { property: "og:description", content: "Finalize seu pedido com segurança." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CheckoutPage,
});

interface FormData {
  nome: string;
  email: string;
  celular: string;
  cpf: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
}

const initial: FormData = {
  nome: "",
  email: "",
  celular: "",
  cpf: "",
  cep: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  uf: "",
};

// Oferecida quando o frete não pode ser calculado (Melhor Envio fora do ar,
// CEP de origem não configurado, produto sem peso/dimensão) — o servidor só
// aceita esse id se confirmar de novo que o frete é mesmo incalculável (ver
// recalculateOrderPricing em mercadopago.functions.ts).
const FALLBACK_SHIPPING_OPTION = {
  id: SHIPPING_FALLBACK_ID,
  name: "A combinar após a compra",
  price: 0,
  deliveryTimeDays: 0,
};

function CheckoutPage() {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const settings = useStoreSettings();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cepLoading, setCepLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [saveAddress, setSaveAddress] = useState(false);

  const [shippingResult, setShippingResult] = useState<ShippingResult | null>(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);

  const [couponInput, setCouponInput] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponResult, setCouponResult] = useState<CouponValidationResult | null>(null);

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    try {
      const result = await applyCoupon({
        data: { code: couponInput.trim(), subtotal: subtotal() },
      });
      setCouponResult(result);
    } catch {
      setCouponResult({
        valid: false,
        reason: "Não foi possível validar o cupom. Tente novamente.",
      });
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponResult(null);
    setCouponInput("");
  };

  useEffect(() => {
    if (items.length === 0) navigate({ to: "/" });
  }, [items.length, navigate]);

  const set = (k: keyof FormData, v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: "" }));
  };

  const validateStep1 = () => {
    const e: Record<string, string> = {};
    if (form.nome.trim().length < 3) e.nome = "Informe seu nome completo";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "E-mail inválido";
    if (form.celular.replace(/\D/g, "").length < 10) e.celular = "Celular inválido";
    if (!validateCPF(form.cpf)) e.cpf = "CPF inválido";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e: Record<string, string> = {};
    if (form.cep.replace(/\D/g, "").length !== 8) e.cep = "CEP inválido";
    if (!form.logradouro) e.logradouro = "Obrigatório";
    if (!form.numero) e.numero = "Obrigatório";
    if (!form.bairro) e.bairro = "Obrigatório";
    if (!form.cidade) e.cidade = "Obrigatório";
    if (!form.uf) e.uf = "Obrigatório";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCEPBlur = async () => {
    const clean = form.cep.replace(/\D/g, "");
    if (clean.length !== 8) return;
    setCepLoading(true);
    const addr = await lookupCEP(clean);
    setCepLoading(false);
    if (!addr) {
      setErrors((e) => ({ ...e, cep: "CEP não encontrado. Verifique e tente novamente." }));
      return;
    }
    setForm((f) => ({
      ...f,
      logradouro: addr.logradouro || f.logradouro,
      bairro: addr.bairro || f.bairro,
      cidade: addr.localidade || f.cidade,
      uf: addr.uf || f.uf,
    }));
  };

  const total = subtotal();
  const discountAmount = couponResult?.valid ? (couponResult.discountAmount ?? 0) : 0;
  const selectedShippingOption =
    selectedServiceId === SHIPPING_FALLBACK_ID
      ? FALLBACK_SHIPPING_OPTION
      : shippingResult?.available && selectedServiceId
        ? shippingResult.options.find((option) => String(option.id) === selectedServiceId)
        : undefined;
  const shippingCost = selectedShippingOption?.price ?? 0;
  const finalTotal = Math.max(0, total - discountAmount + shippingCost);

  const handleNext = async () => {
    const ok = step === 1 ? validateStep1() : validateStep2();
    if (!ok) return;

    if (step === 2) {
      setStep(3);
      setShippingLoading(true);
      setShippingResult(null);
      setSelectedServiceId(null);
      try {
        const result = await calculateShipping({
          data: {
            cep: form.cep,
            items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
          },
        });
        setShippingResult(result);
        if (result.available && result.options.length === 1) {
          setSelectedServiceId(String(result.options[0].id));
        }
      } catch {
        setShippingResult({
          available: false,
          reason: "Não foi possível calcular o frete. Tente novamente.",
        });
      } finally {
        setShippingLoading(false);
      }
      return;
    }

    setStep(step + 1);
  };

  const handlePayment = async () => {
    if (!selectedShippingOption) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const { initPoint } = await startCheckout({
        data: {
          customerName: form.nome,
          customerEmail: form.email,
          customerPhone: form.celular,
          customerCpf: form.cpf,
          shippingAddress: {
            cep: form.cep,
            logradouro: form.logradouro,
            numero: form.numero,
            complemento: form.complemento,
            bairro: form.bairro,
            cidade: form.cidade,
            uf: form.uf,
          },
          items: items.map((i) => ({
            productId: i.productId,
            slug: i.slug,
            name: i.name,
            price: i.price,
            image: i.image,
            quantity: i.quantity,
          })),
          subtotal: total,
          shippingCost: selectedShippingOption.price,
          shippingServiceId: String(selectedShippingOption.id),
          shippingServiceName: selectedShippingOption.name,
          total: finalTotal,
          couponCode: couponResult?.valid ? couponInput.trim() : undefined,
        },
      });

      // Best-effort: não bloqueia o checkout se a gravação do endereço falhar.
      if (saveAddress && user) {
        createAddress({
          data: {
            cep: form.cep,
            logradouro: form.logradouro,
            numero: form.numero,
            complemento: form.complemento,
            bairro: form.bairro,
            cidade: form.cidade,
            uf: form.uf,
          },
        }).catch((err) => console.error("Falha ao salvar endereço", err));
      }

      clearCart();
      window.location.href = initPoint;
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Não foi possível iniciar o pagamento. Tente novamente.",
      );
      setSubmitting(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <h1 className="font-display text-2xl font-bold text-eg-dark mb-6">Finalizar Pedido</h1>

      <div className="flex items-center justify-between mb-8 max-w-lg mx-auto">
        {[1, 2, 3].map((n, idx) => (
          <div key={n} className="flex items-center flex-1 last:flex-none">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                step > n
                  ? "bg-green-600 text-white"
                  : step === n
                    ? "bg-eg-gold text-eg-dark"
                    : "bg-muted text-muted-foreground"
              }`}
            >
              {step > n ? <Check className="h-4 w-4" /> : n}
            </div>
            {idx < 2 && (
              <div className={`h-1 flex-1 mx-2 ${step > n ? "bg-green-600" : "bg-muted"}`} />
            )}
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="rounded-lg bg-white border border-border p-6">
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-display font-bold text-eg-dark">Dados Pessoais</h2>
              <Field label="Nome completo" error={errors.nome}>
                <input
                  className="input"
                  value={form.nome}
                  onChange={(e) => set("nome", e.target.value)}
                />
              </Field>
              <Field label="E-mail" error={errors.email}>
                <input
                  type="email"
                  className="input"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Celular" error={errors.celular}>
                  <input
                    className="input"
                    value={form.celular}
                    placeholder="(19) 99999-9999"
                    onChange={(e) => set("celular", formatPhone(e.target.value))}
                  />
                </Field>
                <Field label="CPF" error={errors.cpf}>
                  <input
                    className="input"
                    value={form.cpf}
                    placeholder="000.000.000-00"
                    onChange={(e) => set("cpf", formatCPF(e.target.value))}
                  />
                </Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-display font-bold text-eg-dark">Endereço de Entrega</h2>
              {user && (
                <SavedAddressPicker onSelect={(addr) => setForm((f) => ({ ...f, ...addr }))} />
              )}
              <Field label="CEP" error={errors.cep}>
                <div className="relative">
                  <input
                    className="input"
                    value={form.cep}
                    placeholder="00000-000"
                    onChange={(e) => set("cep", formatCEP(e.target.value))}
                    onBlur={handleCEPBlur}
                  />
                  {cepLoading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </Field>
              <Field label="Logradouro" error={errors.logradouro}>
                <input
                  className="input"
                  value={form.logradouro}
                  onChange={(e) => set("logradouro", e.target.value)}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Número" error={errors.numero}>
                  <input
                    className="input"
                    value={form.numero}
                    onChange={(e) => set("numero", e.target.value)}
                  />
                </Field>
                <Field label="Complemento (opcional)">
                  <input
                    className="input"
                    value={form.complemento}
                    onChange={(e) => set("complemento", e.target.value)}
                  />
                </Field>
              </div>
              <Field label="Bairro" error={errors.bairro}>
                <input
                  className="input"
                  value={form.bairro}
                  onChange={(e) => set("bairro", e.target.value)}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-[1fr_100px]">
                <Field label="Cidade" error={errors.cidade}>
                  <input
                    className="input"
                    value={form.cidade}
                    onChange={(e) => set("cidade", e.target.value)}
                  />
                </Field>
                <Field label="UF" error={errors.uf}>
                  <input
                    className="input uppercase"
                    maxLength={2}
                    value={form.uf}
                    onChange={(e) => set("uf", e.target.value.toUpperCase())}
                  />
                </Field>
              </div>
              {user && (
                <label className="flex items-center gap-2 text-sm text-eg-dark">
                  <input
                    type="checkbox"
                    checked={saveAddress}
                    onChange={(e) => setSaveAddress(e.target.checked)}
                  />
                  Salvar este endereço para próximas compras
                </label>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-display font-bold text-eg-dark">Frete</h2>
              {shippingLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Calculando opções de frete...
                </div>
              )}
              {!shippingLoading && shippingResult && !shippingResult.available && (
                <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="text-sm text-red-700">{shippingResult.reason}</p>
                  <div className="flex flex-wrap items-center gap-4">
                    <button
                      type="button"
                      onClick={() => setSelectedServiceId(SHIPPING_FALLBACK_ID)}
                      className={`rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                        selectedServiceId === SHIPPING_FALLBACK_ID
                          ? "border-eg-gold bg-eg-gold/10 text-eg-dark"
                          : "border-eg-dark text-eg-dark hover:bg-eg-dark hover:text-white"
                      }`}
                    >
                      Continuar sem calcular o frete agora
                    </button>
                    <a
                      href={whatsappLink(
                        settings.whatsapp_number,
                        `Olá! Meu CEP é ${form.cep} e não consegui calcular o frete no checkout.`,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-green-700 hover:underline"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Falar no WhatsApp
                    </a>
                  </div>
                  {selectedServiceId === SHIPPING_FALLBACK_ID && (
                    <p className="text-xs text-muted-foreground">
                      O frete será combinado com você após a compra, por WhatsApp.
                    </p>
                  )}
                </div>
              )}
              {!shippingLoading && shippingResult?.available && (
                <div className="space-y-2">
                  {shippingResult.options.map((option) => (
                    <label
                      key={option.id}
                      className={`flex cursor-pointer items-center justify-between gap-3 rounded-md border p-3 text-sm transition-colors ${
                        selectedServiceId === String(option.id)
                          ? "border-eg-gold bg-eg-gold/10"
                          : "border-border"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="shipping-option"
                          checked={selectedServiceId === String(option.id)}
                          onChange={() => setSelectedServiceId(String(option.id))}
                        />
                        <span>
                          {option.name}
                          <span className="block text-xs text-muted-foreground">
                            Prazo estimado: {option.deliveryTimeDays} dia(s) útil(eis)
                          </span>
                        </span>
                      </span>
                      <span className="font-semibold text-eg-dark">{formatBRL(option.price)}</span>
                    </label>
                  ))}
                </div>
              )}

              <h2 className="font-display font-bold text-eg-dark pt-2">Pagamento</h2>
              <div className="rounded-lg border border-eg-gold/40 bg-eg-gold/10 p-4">
                <p className="text-sm font-semibold text-eg-dark">Pix ou cartão de crédito</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Você será redirecionado ao Mercado Pago para concluir o pagamento com segurança.
                  Assim que for aprovado, seu pedido é confirmado automaticamente.
                </p>
              </div>
              {submitError && <p className="text-xs text-red-600">{submitError}</p>}
              <button
                type="button"
                onClick={handlePayment}
                disabled={submitting || !selectedShippingOption}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-3 font-semibold text-white hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <ShieldCheck className="h-5 w-5" />
                )}
                Pagar com Mercado Pago
              </button>
            </div>
          )}

          <div className="mt-6 flex justify-between gap-3">
            <button
              type="button"
              disabled={step === 1}
              onClick={() => setStep(step - 1)}
              className="rounded-md border border-eg-dark px-4 py-2 text-sm font-medium text-eg-dark hover:bg-eg-dark hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Voltar
            </button>
            {step < 3 && (
              <button
                type="button"
                onClick={handleNext}
                className="rounded-md bg-eg-gold px-6 py-2 text-sm font-semibold text-eg-dark hover:bg-eg-goldhover transition-colors"
              >
                Próximo
              </button>
            )}
          </div>
        </div>

        <aside className="rounded-lg bg-white border border-border p-6 h-fit space-y-3">
          <h2 className="font-display font-bold text-eg-dark">Seu Pedido</h2>
          <ul className="space-y-2 text-sm">
            {items.map((i) => (
              <li key={i.productId} className="flex justify-between gap-2">
                <span className="line-clamp-2">
                  {i.quantity}x {i.name}
                </span>
                <span className="font-medium whitespace-nowrap">
                  {formatBRL(i.price * i.quantity)}
                </span>
              </li>
            ))}
          </ul>
          <div className="border-t border-border pt-3 space-y-2">
            {couponResult?.valid ? (
              <div className="flex items-center justify-between gap-2 rounded-md bg-green-50 px-3 py-2 text-xs text-green-800">
                <span className="flex items-center gap-1 font-medium">
                  <Tag className="h-3.5 w-3.5" />
                  Cupom {couponInput.trim().toUpperCase()} aplicado
                </span>
                <button type="button" onClick={removeCoupon} aria-label="Remover cupom">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  className="input"
                  placeholder="Cupom de desconto"
                  value={couponInput}
                  onChange={(e) => {
                    setCouponInput(e.target.value);
                    setCouponResult(null);
                  }}
                />
                <button
                  type="button"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponInput.trim()}
                  className="shrink-0 rounded-md border border-eg-dark px-3 py-2 text-xs font-semibold text-eg-dark hover:bg-eg-dark hover:text-white disabled:opacity-50 transition-colors"
                >
                  {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar"}
                </button>
              </div>
            )}
            {couponResult && !couponResult.valid && (
              <p className="text-xs text-red-600">{couponResult.reason}</p>
            )}
          </div>
          <div className="border-t border-border pt-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{formatBRL(total)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-sm text-green-700">
                <span>Desconto</span>
                <span>-{formatBRL(discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Frete</span>
              <span className={selectedShippingOption ? undefined : "text-muted-foreground"}>
                {selectedServiceId === SHIPPING_FALLBACK_ID
                  ? "A combinar"
                  : selectedShippingOption
                    ? formatBRL(selectedShippingOption.price)
                    : "A calcular"}
              </span>
            </div>
            <div className="flex justify-between font-bold text-base pt-2">
              <span>Total</span>
              <span className="text-eg-goldtext">{formatBRL(finalTotal)}</span>
            </div>
          </div>
        </aside>
      </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.375rem;
          border: 1px solid var(--border);
          background: white;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .input:focus {
          border-color: var(--eg-gold);
          box-shadow: 0 0 0 2px color-mix(in oklab, var(--eg-gold) 30%, transparent);
        }
      `}</style>
    </div>
  );
}

function SavedAddressPicker({
  onSelect,
}: {
  onSelect: (addr: Omit<CustomerAddress, "id" | "label" | "is_default">) => void;
}) {
  const { data: addresses } = useQuery(myAddressesQuery());

  if (!addresses || addresses.length === 0) return null;

  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-eg-dark">Usar endereço salvo</span>
      <select
        className="input"
        defaultValue=""
        onChange={(e) => {
          const addr = addresses.find((a) => a.id === e.target.value);
          if (addr) onSelect(addr);
        }}
      >
        <option value="" disabled>
          Selecione um endereço ou preencha abaixo
        </option>
        {addresses.map((addr) => (
          <option key={addr.id} value={addr.id}>
            {addr.label ? `${addr.label} — ` : ""}
            {addr.logradouro}, {addr.numero} — {addr.cidade}/{addr.uf}
          </option>
        ))}
      </select>
    </label>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-eg-dark">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  );
}
